import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import PropTypes from 'prop-types';

import styled from 'styled-components';
import { Button, Separator, Text } from '@cuemath/comps';
import { showNotification } from 'actions/notification';
import { openModal, closeModal } from 'actions/modal';
import {
  patch as studentCoursePatch,
  invalidateItem as invalidateStudentCourses,
} from 'actions/student-course';

import {
  Message,
  Head,
  FieldWrapper,
  renderFormField as renderFormFieldModule,
  getOptions,
} from '../modules/form';
import {
  FIELD_TYPES,
  getInputChangeMethod,
  getOnChangeMethod,
  studentCourseEditFields,
} from '../constants/form';
import { dateObjToEpoch, epochToDate } from '../utils';
import { invalidateStudentCoursesGetAll } from 'actions/studentsv2';

const HeadWrapper = styled(Head)`
  border-bottom: 1px solid #333;
`;

class StudentCourseEditModal extends PureComponent {
  static propTypes = {
    actions: PropTypes.shape({
      studentCoursePatch: PropTypes.func.isRequired,
      closeModal: PropTypes.func.isRequired,
      invalidateStudentCourses: PropTypes.func.isRequired,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      errorText: null,
      message: null,
      studentCoursePatch: false,
      form: this.getFormDefaults(),
      formFields: studentCourseEditFields,
    };
    studentCourseEditFields.forEach(field => {
      const methodName = getOnChangeMethod(field.name);
      this[methodName] = this.onChange.bind(this, field.name);
    });
  }

  static getDerivedStateFromProps(props, state) {
    const { studentCourseStore, studentCourse, actions } = props;
    const { formFields, studentCoursePatch } = state;
    const newState = {};
    newState.formFields = formFields;
    const studentCoursePatchData = store.patch(
      studentCourseStore,
      (studentCourse.id, studentCourse.id),
    );
    if (studentCoursePatchData && studentCoursePatchData.resolved && studentCoursePatch) {
      if (studentCoursePatchData.resolved.isPatched) {
        newState.message = 'Student Course successfully updated';
        newState.errorText = null;
        actions.invalidateStudentCourses({ id: studentCourse.student_id });
        invalidateStudentCoursesGetAll();
        actions.closeModal();
      } else if (studentCoursePatchData.resolved.isPatchingFailed) {
        const error = store.getError(studentCoursePatchData.resolved);
        newState.errorText = error ? error.message : 'Update failed, please try again';
        newState.message = null;
      }
      newState.studentCoursePatch = false;
    }
    return newState;
  }

  getFormDefaults = () => {
    const formDefaults = {};
    const { studentCourse } = this.props;

    studentCourseEditFields.forEach(field => {
      if (field.fieldType === FIELD_TYPES.SELECT)
        field.choices = getOptions({
          field: field.name,
        });
      formDefaults[field.name] =
        !!studentCourse && studentCourse[field.param]
          ? studentCourse[field.param]
          : field.default;
    });
    const dateFields = ['class_start_date', 'validity_till_date'];
    dateFields.forEach(field => {
      formDefaults[field] = epochToDate(studentCourse[field], 'YYYY-MM-DD');
    });

    return formDefaults;
  };

  onChange = (key, value) => {
    const { form } = this.state;

    const newForm = { ...form };
    newForm[key] = value;

    const stateChanges = { errorText: null, form: newForm };
    this.setState(() => ({ ...stateChanges }));
  };

  onOptionChange = (key, value) => {
    const { form } = this.state;
    const { actions, studentCourse } = this.props;
    const shouldNotAllowClassRatioUpgrade =
      key === 'class_ratio' &&
      [1, 2].includes(value) &&
      studentCourse.class_ratio !== value;

    if (shouldNotAllowClassRatioUpgrade) {
      actions.showNotification({
        text: 'You cannot upgrade class ratio',
        duration: 5,
        showCloseIcon: true,
      });
      return;
    }
    const newForm = { ...form };
    newForm[key] = value;

    const stateChanges = { errorText: null, form: newForm };
    this.setState(() => ({ ...stateChanges }));
  };

  renderFormField = field => {
    const { form } = this.state;
    const disabled = field.disabled || !field.editable;
    const required = field.required;
    const placeholder = disabled ? '' : field.placeholder || field.label;

    return renderFormFieldModule({
      ...field,
      value: form[field.name],
      onChange: this[getInputChangeMethod(field)],
      required,
      disabled,
      placeholder,
    });
  };

  updateStudentCourse = () => {
    const { form } = this.state;
    const { studentCourse, actions } = this.props;
    const { classes_balance: classesBalance } = form || {};
    const patchData = {};
    this.setState({ errorText: '' });
    if (form['course_id'] !== studentCourse['course_id']) {
      patchData['course_id'] = form['course_id'];
    }
    if (form['primary_teacher_id'] !== studentCourse['primary_teacher_id']) {
      patchData['primary_teacher_id'] = form['primary_teacher_id'];
    }
    if (
      isNaN(form['validity_till_date']) &&
      form['validity_till_date'] !==
        epochToDate(studentCourse['validity_till_date'], 'YYYY-MM-DD')
    ) {
      patchData['validity_till_date'] = dateObjToEpoch(form['validity_till_date']);
    }
    if (
      isNaN(form['class_start_date']) &&
      form['class_start_date'] !==
        epochToDate(studentCourse['class_start_date'], 'YYYY-MM-DD')
    ) {
      patchData['class_start_date'] = dateObjToEpoch(form['class_start_date']);
    }
    if (!form['student_course_update_reason'] || !form['remarks']) {
      this.setState({ errorText: 'Update category / remarks are mandatory' });
      return;
    }
    if (!classesBalance) {
      this.setState({ errorText: 'Classes balance cannot be empty' });
      return;
    }
    if (studentCourse['classes_balance'] != classesBalance) {
      patchData['classes_balance'] = classesBalance;
    }
    if (form['classes_per_week'] !== studentCourse['classes_per_week']) {
      patchData['classes_per_week'] = form['classes_per_week'];
    }
    if (form['class_ratio'] !== studentCourse['class_ratio']) {
      patchData['class_ratio'] = form['class_ratio'];
    }

    if (Object.keys(patchData).length > 0) {
      patchData['student_course_id'] = studentCourse.id;
      patchData['reason'] = {
        category: form['student_course_update_reason'],
        remarks: form['remarks'],
      };
      this.setState({ studentCoursePatch: true }, () => {
        actions.studentCoursePatch(studentCourse.id, studentCourse.id, patchData);
      });
    }
  };

  renderFormFields = () => {
    const { formFields, errorText, message } = this.state;
    let renderedFields = formFields.map(field => this.renderFormField(field));

    return (
      <FieldWrapper>
        {renderedFields}
        {errorText && <Message error={true}>{errorText}</Message>}
        {message && <Message>{message}</Message>}
        <Separator transparent height="25" />
        <Button label="Submit" onClick={() => this.updateStudentCourse()} />
      </FieldWrapper>
    );
  };

  onSubmit = event => {
    event.preventDefault();
  };

  render() {
    return (
      <form onSubmit={this.onSubmit}>
        <HeadWrapper>
          <Text size="xlarge" align="center">
            Edit
          </Text>
        </HeadWrapper>

        {this.renderFormFields()}

        <Separator transparent height="10" />
      </form>
    );
  }
}

export default connect(
  ({ studentCourse: studentCourseStore }) => ({
    studentCourseStore,
  }),
  dispatch => ({
    actions: bindActionCreators(
      {
        openModal,
        closeModal,
        studentCoursePatch,
        invalidateStudentCourses,
        invalidateStudentCoursesGetAll,
        showNotification,
      },
      dispatch,
    ),
  }),
)(StudentCourseEditModal);
